import graphql__4bb81e507bd8d2351ea9 from "./__generated__/ContributionModal_contribution.graphql.js";import { useEffect } from "react";
import { graphql, useFragment } from "react-relay";
import classNames from "classnames";

import { Desktop, NonDesktop } from "../Utils/ResponsiveQueries";
import Modal from "../Modals/Modal";
import RichText from "../Utils/RichText";
import ContributionContent from "./ContributionContent";
import AnswerFormField from "../Answer/AnswerFormField";
import CommentsPanelManager from "../Comments/CommentsPanelManager";
import ContributionModalTheme from "../ParticipativeModule/Visualizations/ContributionModalTheme";
import ContributionModalLinkedContributions from "../ParticipativeModule/Visualizations/ContributionModalLinkedContributions";
import ContributionModalEvent from "../ParticipativeModule/Visualizations/ContributionModalEvent";
import { isUnauthorized } from "../Utils/authorizations";
import ContributionLocationMap from "../ParticipativeModule/ContributionLocationMap";
import { useNavigateTo } from "../Routes/RoutesContext";
import Report from "../Reports/Report";
import ContributionHeader from "./ContributionHeader";
import ContributionModalReactions from "./ContributionModalReactions";

const ContributionModal = props => {
  const contribution = useFragment(ContributionModal_contribution, props.contribution);

  const { title, additionalDisplayableFields } = contribution;

  const { commentsDisplayAuthorized, displaysDetailsAsModal } =
    contribution.authorizations;

  const { slug: participativeModuleSlug } = contribution.participativeModule;

  const navigateTo = useNavigateTo();

  // This avoids some edge cases where the modal is accessed although it shouldn't be. The
  // experienced case was : reloading a contribution edit form page and closing the modal,
  // which automatically returns to the modal because of the fallback modal closing
  // mechanism from ModalManager#findClosestMatchingParentLocation when there's no
  // non-modal location registered during the browsing session, which happens to be the
  // case when you reload a page while on a modal route.
  useEffect(() => {
    if (displaysDetailsAsModal) return;
    navigateTo("participativeModule", { participativeModuleSlug });
  }, [displaysDetailsAsModal, navigateTo, participativeModuleSlug]);

  if (!displaysDetailsAsModal) return null;

  const hasLeftColumnDisplayed =
    contribution.theme || contribution.events.length > 0 || contribution.hasLocation;

  const className = classNames("contribution-modal", {
    "contribution-modal-one-column": !hasLeftColumnDisplayed
  });

  const mainColumnClassName = classNames({
    "col-md-8": hasLeftColumnDisplayed,
    "col-12": !hasLeftColumnDisplayed
  });

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      className={className}
      title={title}
      displayTitle={false}
    >
      <div className="row">
        {hasLeftColumnDisplayed && (
          <div className="col-md-4">
            <ContributionModalTheme contribution={contribution} />

            <ContributionLocationMap
              contribution={contribution}
              className="margin-top-xs rounded overflow-hidden"
            />

            <Desktop>
              <ContributionModalEvent contribution={contribution} />
              <ContributionModalLinkedContributions contribution={contribution} />
            </Desktop>
          </div>
        )}

        <div className={mainColumnClassName}>
          <article className="box contribution margin-top-xs">
            <ContributionHeader
              contribution={contribution}
              className="margin-bottom-sm"
            />

            <h1 className="title text-title-section margin-bottom-xs">{title}</h1>

            <div>
              <div className="gradient"></div>

              <RichText className="description padding-y-xs">
                <ContributionContent contribution={contribution} />
              </RichText>

              {additionalDisplayableFields.map(field => (
                <AnswerFormField key={field.id} field={field} />
              ))}
            </div>

            <div className="reactions margin-top-xs d-flex align-items-center justify-content-between">
              <ContributionModalReactions contribution={contribution} />

              <div className="d-flex justify-content-end">
                <div>
                  <Report reportable={contribution} />
                </div>
              </div>
            </div>
          </article>

          {!isUnauthorized(commentsDisplayAuthorized) && (
            <CommentsPanelManager
              commentableId={contribution.id}
              className="margin-top-xs vizualization-comments-pannel"
            />
          )}
        </div>
      </div>

      <NonDesktop>
        <ContributionModalEvent contribution={contribution} />
        <ContributionModalLinkedContributions contribution={contribution} />
      </NonDesktop>
    </Modal>
  );
};

export default ContributionModal;

const ContributionModal_contribution = graphql__4bb81e507bd8d2351ea9;
